/* eslint-disable consistent-return */
import { Meteor } from 'meteor/meteor';
import { check } from 'meteor/check';
import { HTTP } from 'meteor/http';
import {
  cloneDeep,
  forEach,
  get,
  isEmpty,
  isNil,
  map,
  replace,
  reverse,
  sortBy,
  split,
  toNumber
} from 'lodash';

import * as atlasOps from '../../Atlas/atlas-operations';
import Atlas from '../../Atlas/Atlas';
import { insert as insertMSE } from '../../MapServerElements/server/map-server-element-methods-server';
import * as taskOps from '../task-operations';
import Tasks from '../Tasks';
import { TaskSchemaOut, TaskSchemaIn, TaskSchemaMonitor } from '../TaskSchema';
import { STATUS_TYPES } from '../taskConstants';

import checkAuthentication from '../../../modules/server/check-authentication';

import { isHTTPResultOK } from '../../../modules/fetch-utilites';
import handleMethodException from '../../../modules/handle-method-exception';
import {
  cleanTasks,
  getMapServerElementsFromTasklist,
  checkTaskNodes
} from '../../../modules/tasks-utilities';

const { isBeta } = Meteor.settings.public;
const {
  taskListAPI: TASKS_API,
  runTaskAPI: RUN_TASK_API,
  taskMonitorAPI: TASK_MONITOR_API,
  getTaskResultsAPI: GET_RESULTS_API,
  cancelTaskAPI: CANCEL_TASK_API,
  pauseTaskAPI: PAUSE_TASK_API,
  resumeTaskAPI: RESUME_TASK_API,
  getAIEntitiesListAPI: GET_AI_ENTITIES_API,
  getFocusedTrainParamsListAPI: GET_FOCUSED_TRAIN_PARAMS_API,
  taskFTPParameters: TASK_FTP_PARAMETERS
} = Meteor.settings.private;

const DEBUG_SERVER_IP = split(RUN_TASK_API, '/')[2];
const RUNNING_GROUP = 'Running Tasks';

const editTaskSchema = Tasks.schema.omit('owner', 'createdAt', 'updatedAt', 'deleted');
editTaskSchema.extend({ _id: String });

/**
 * Fetchs one task from the database.
 *
 * @param {string} taskId - The task id.
 * @param {string} [authenticate] - Flag to check authentication.
 * @returns {object} Return the task from the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const fetchOne = (taskId, authenticate = true) => {
  try {
    if (authenticate) checkAuthentication();
    return Tasks.findOne(taskId);
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Inserts the task in the database.
 *
 * @param {object} arg - The arguments object.
 * @param {object} arg.taskObject - The task object.
 * @param {string} [arg.authenticate] - Flag to check authentication.
 * @param {string} [arg.userId] - The id of the user owner of the task.
 * @returns {string} Return the id of the new task.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = ({ authenticate = true, taskObject, userId = Meteor.userId() }) => {
  try {
    if (authenticate) checkAuthentication();
    Tasks.schema.validate(taskObject, {
      check,
      keys: [
        'startedAt',
        'finishedAt',
        'status',
        'progress',
        'processId',
        'name',
        'label',
        'group',
        'links',
        'nodes'
      ]
    });
    return Tasks.insert({ owner: userId, ...taskObject });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the task in the database.
 *
 * @param {object} taskObject - The task object.
 * @param {string} [authenticate] - Flag to check authentication.
 * @returns {string} Return the id of the updated task.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = (taskObject, authenticate = true) => {
  try {
    if (authenticate) checkAuthentication();
    const clonedTask = cloneDeep(taskObject);
    delete clonedTask.owner;
    delete clonedTask.createdAt;
    delete clonedTask.updatedAt;
    delete clonedTask.deleted;
    if (!isNil(clonedTask.progress)) clonedTask.progress = toNumber(clonedTask.progress);
    editTaskSchema.validate(clonedTask);
    const taskId = taskOps.getId(clonedTask);
    Tasks.update(taskId, { $set: clonedTask });
    return taskId;
    // Return _id so we can redirect to task after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the task in the database.
 *
 * @param {string} taskId - The task id.
 * @returns {string} Return the id of the task.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = taskId => {
  check(taskId, String);
  try {
    checkAuthentication();
    return Tasks.update(taskId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the task in the database.
 *
 * @param {string} taskId - The task id.
 * @returns {string} Return the id of the task.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = taskId => {
  check(taskId, String);
  try {
    checkAuthentication();
    return Tasks.update(taskId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the task from database.
 *
 * @param {string} taskId - The task id.
 * @returns {string} Return the id of the deleted task.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = taskId => {
  check(taskId, String);
  try {
    checkAuthentication();
    return Tasks.remove(taskId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Gets the tasklist from the geoserver and returns ir after validation.
 *
 * @param {string} [debugIP] - The IP for debuggin purposes.
 * @returns {TaskSchemaIn[]} Return the array of tasks for this user.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const getTaskList = async debugIP => {
  check(debugIP, String);
  try {
    checkAuthentication();
    let finalAPI = TASKS_API;
    if (isBeta && !isEmpty(debugIP)) finalAPI = replace(finalAPI, DEBUG_SERVER_IP, debugIP);
    const response = HTTP.post(finalAPI, {
      headers: { 'Content-Type': 'application/json' },
      data: { user: Meteor.userId() }
    });
    const tasks = isHTTPResultOK(response);
    if (isNil(tasks)) throw new Error('Empty Response');

    const validatedTasks = cleanTasks({ tasklist: tasks, schema: TaskSchemaIn });
    return map(validatedTasks, task => checkTaskNodes(task));
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Sends the task to run in the geoserver.
 *
 * @param {object} arg - The arguments object.
 * @param {string} [arg.authenticate] - Flag to check authentication.
 * @param {string} [arg.debugIP] - The IP for debuggin purposes.
 * @param {string} [arg.isTasker] - Flag to check if task comes from tasker.
 * @param {string} [arg.itemForOwner] - An object with the parameters to get the task owner ({ id, collectionName }).
 * @param {TaskSchemaOut} arg.taskToRun - The task to run.
 * @returns {string} The task id.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const runTask = async ({
  authenticate = true,
  debugIP,
  isTasker = true,
  itemForOwner = {},
  taskToRun
}) => {
  check(debugIP, String);
  try {
    if (authenticate) checkAuthentication();

    let userId = Meteor.userId();
    if (!isTasker) {
      let item = {};
      switch (itemForOwner.collectionName) {
        case 'atlas':
          item = Atlas.findOne(itemForOwner.id);
          break;
        case 'tasks':
          item = Tasks.findOne(itemForOwner.id);
          break;
        default:
          break;
      }
      userId = get(item, 'owner', userId);
    }

    const taskToSend = cloneDeep(taskToRun);
    const cleanedTask = await TaskSchemaOut.clean(taskToSend);
    let finalAPI = RUN_TASK_API;
    if ((isBeta || !isTasker) && !isEmpty(debugIP))
      finalAPI = replace(finalAPI, DEBUG_SERVER_IP, debugIP);
    const taskId = insert({ authenticate, taskObject: cleanedTask, userId });
    const taskInDatabase = fetchOne(taskId, authenticate);
    TaskSchemaOut.validate(taskInDatabase);
    const response = HTTP.post(finalAPI, {
      headers: { 'Content-Type': 'application/json' },
      data: { user: userId, json_graph: taskInDatabase }
    });
    const result = isHTTPResultOK(response);
    if (isNil(result) || isEmpty(result)) throw new Error('Empty Response');

    const processId = result.task_id;
    // FIXME: This processId is a string that can contain " chars, pe. ({ task_id: 'Mr0>qu¿Yl"' })
    if (isNil(processId) || isEmpty(processId)) throw new Error('Empty Response');

    taskInDatabase.processId = processId;
    taskInDatabase.group = RUNNING_GROUP;
    update(taskInDatabase);
    return taskId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Get task monitor for current user.
 *
 * @param {string} arg - The arguments object.
 * @param {string} [arg.authenticate] - Flag to check authentication.
 * @param {string} [arg.debugIP] - The IP for debuggin purposes.
 * @param {string} [arg.isTasker] - Flag to check if task comes from tasker.
 * @param {string} [arg.itemForOwner] - An object with the parameters to get the task owner ({ id, collectionName }).
 * @returns {TaskSchemaMonitor[]} Return the array of tasks for this user in the task monitor.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const getTaskMonitor = async ({
  authenticate = true,
  debugIP,
  isTasker = true,
  itemForOwner = {}
}) => {
  check(debugIP, String);
  try {
    if (authenticate) checkAuthentication();

    let userId = Meteor.userId();
    if (!isTasker) {
      let item = {};
      switch (itemForOwner.collectionName) {
        case 'atlas':
          item = Atlas.findOne(itemForOwner.id);
          break;
        case 'tasks':
          item = Tasks.findOne(itemForOwner.id);
          break;

        default:
          break;
      }
      userId = get(item, 'owner', userId);
    }

    let finalAPI = TASK_MONITOR_API;
    if ((isBeta || !isTasker) && !isEmpty(debugIP))
      finalAPI = replace(finalAPI, DEBUG_SERVER_IP, debugIP);
    const response = HTTP.post(finalAPI, {
      headers: { 'Content-Type': 'application/json' },
      data: { user: userId }
    });
    const tasks = reverse(sortBy(isHTTPResultOK(response), task => taskOps.getStartDate(task)));
    return cleanTasks({ tasklist: tasks, schema: TaskSchemaMonitor });
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Runs the task selected.
 * A traves del microservicio de task monitor solo
 * nos envian la informacion de las tareas.
 * DESPUES DE RECIBIR ESTAS TAREAS SE BORRAN.
 * HAY QUE SACAR TODOS LOS DATOS.
 * CREAR LOS MAP SERVER ELEMENTS Y PONERLA COMO EN OTRA LISTA CONFORME YA ESTA HECHA.
 *
 * @param {string} [debugIP] - The IP for debuggin purposes.
 * @returns {TaskSchemaMonitor[]} Return the array of tasks for this user.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const getTaskResults = async debugIP => {
  check(debugIP, String);
  try {
    checkAuthentication();
    let finalAPI = GET_RESULTS_API;
    if (isBeta && !isEmpty(debugIP)) finalAPI = replace(finalAPI, DEBUG_SERVER_IP, debugIP);
    const response = HTTP.post(finalAPI, {
      headers: { 'Content-Type': 'application/json' },
      data: { user: Meteor.userId() }
    });
    const tasks = isHTTPResultOK(response);
    const validatedTasks = cleanTasks({ tasklist: tasks, schema: TaskSchemaMonitor });
    const resultingMapServerElements = getMapServerElementsFromTasklist(validatedTasks);

    let hasError = false;
    let errorObject = null;
    forEach(validatedTasks, resultTask => {
      try {
        // eslint-disable-next-line no-param-reassign
        resultTask.group = 'Saved Tasks';
        update(resultTask);
      } catch (error) {
        console.error(error);
        hasError = true;
        errorObject = error;
      }
    });
    forEach(resultingMapServerElements, resultingMapServerElement => {
      try {
        insertMSE(resultingMapServerElement, Meteor.userId());
      } catch (error) {
        console.error(error);
        hasError = true;
        errorObject = error;
      }
    });
    if (hasError) throw errorObject;

    return resultingMapServerElements;
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Updates tasks labels on user request.
 *
 * @param {string} taskId - The task id.
 * @param {string} newLabel - The new task label.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateTaskLabel = (taskId, newLabel) => {
  check(taskId, String);
  try {
    checkAuthentication();
    Tasks.update(taskId, { $set: { label: newLabel } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Cancels a user task in the geoserver.
 *
 * @param {object} arg - The argument object.
 * @param {string} arg.taskId - The task id.
 * @param {string} [arg.debugIP] - The IP for debuggin purposes.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const cancel = async ({ debugIP, taskId }) => {
  check(debugIP, String);
  try {
    checkAuthentication();
    const task = Tasks.findOne(taskId);
    let finalAPI = CANCEL_TASK_API;
    if (isBeta && !isEmpty(debugIP)) finalAPI = replace(finalAPI, DEBUG_SERVER_IP, debugIP);
    const response = HTTP.post(finalAPI, {
      headers: { 'Content-Type': 'application/json' },
      data: { user: Meteor.userId(), task_id: taskOps.getProcessId(task) }
    });
    const serverTask = isHTTPResultOK(response);
    if (!isNil(serverTask)) Tasks.remove(taskId);
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Pauses a user task in the geoserver.
 *
 * @param {object} arg - The argument object.
 * @param {string} arg.taskId - The task id.
 * @param {string} [arg.debugIP] - The IP for debuggin purposes.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const pause = async ({ debugIP, taskId }) => {
  check(debugIP, String);
  try {
    checkAuthentication();
    const task = Tasks.findOne(taskId);
    let finalAPI = PAUSE_TASK_API;
    if (isBeta && !isEmpty(debugIP)) finalAPI = replace(finalAPI, DEBUG_SERVER_IP, debugIP);
    const response = HTTP.post(finalAPI, {
      headers: { 'Content-Type': 'application/json' },
      data: { user: Meteor.userId(), task_id: taskOps.getProcessId(task) }
    });
    const serverTask = isHTTPResultOK(response);
    if (!isNil(serverTask)) Tasks.update(taskId, { $set: { status: STATUS_TYPES.PAUSED } });
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Resumes a user task in the geoserver.
 *
 * @param {object} arg - The argument object.
 * @param {string} arg.taskId - The task id.
 * @param {string} [arg.debugIP] - The IP for debuggin purposes.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const resume = async ({ debugIP, taskId }) => {
  check(debugIP, String);
  try {
    checkAuthentication();
    const task = Tasks.findOne(taskId);
    let finalAPI = RESUME_TASK_API;
    if (isBeta && !isEmpty(debugIP)) finalAPI = replace(finalAPI, DEBUG_SERVER_IP, debugIP);
    const response = HTTP.post(finalAPI, {
      headers: { 'Content-Type': 'application/json' },
      data: { user: Meteor.userId(), task_id: taskOps.getProcessId(task) }
    });
    const serverTask = isHTTPResultOK(response);
    if (!isNil(serverTask)) Tasks.update(taskId, { $set: { status: STATUS_TYPES.INPROGRESS } });
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Gets the entities list for AI to detect.
 *
 * @param {string} [debugIP] - The IP for debuggin purposes.
 * @returns {Array} The array with the entities list.
 * @throws {Error} Meteor Error if anything goes wrong.
 */

export const getAIEntitiesList = async (debugIP, isTasker) => {
  try {
    let finalAPI = GET_AI_ENTITIES_API;
    if ((isBeta || !isTasker) && !isEmpty(debugIP))
      finalAPI = replace(finalAPI, DEBUG_SERVER_IP, debugIP);
    const response = HTTP.get(finalAPI, {
      headers: { 'Content-Type': 'application/json' },
      data: {}
    });
    return isHTTPResultOK(response);
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Gets the parameters list for the localized training task.
 *
 * @param {string} [debugIP] - The IP for debuggin purposes.
 * @param {boolean} [isTasker] - Flag to check if task comes from Tasker.
 * @returns {Array} The array with the parameters list.
 * @throws {Error} Meteor Error if anything goes wrong.
 */

export const getFocusedTrainParamsList = async (debugIP, isTasker) => {
  try {
    let finalAPI = GET_FOCUSED_TRAIN_PARAMS_API;
    if ((isBeta || !isTasker) && !isEmpty(debugIP))
      finalAPI = replace(finalAPI, DEBUG_SERVER_IP, debugIP);
    const response = HTTP.get(finalAPI, {
      headers: { 'Content-Type': 'application/json' },
      data: {}
    });
    return isHTTPResultOK(response);
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Gets the parameters list for the localized training task.
 *
 * @param {string} [url] - The IP for debuggin purposes.
 * @param {object} [data] - Flag to check if task comes from Tasker.
 * @returns {Array} The array with the parameters list.
 * @throws {Error} Meteor Error if anything goes wrong.
 */

export const getDataSet = async ({ url, data }) => {
  try {
    // eslint-disable-next-line no-unused-vars
    const response = HTTP.post(url, {
      headers: { 'Content-Type': 'application/json' },
      data
    });
    return '';
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Gets the parameters list for the localized training task.
 *
 * @param {string} [url] - The IP for debuggin purposes.
 * @param {object} [data] - Flag to check if task comes from Tasker.
 * @returns {Array} The array with the parameters list.
 * @throws {Error} Meteor Error if anything goes wrong.
 */

export const getListJson = async ({ url, data }) => {
  try {
    const response = HTTP.post(url, {
      headers: { 'Content-Type': 'application/json' },
      data
    });
    const result = isHTTPResultOK(response);
    const loadParameters = get(result, 'message', []);
    return loadParameters;
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Gets the number of AI tasks with the same name.
 *
 * @param {object} arg - The arguments object.
 * @param {object} arg.atlas - The atlas the task comes from.
 * @param {boolean} arg.isPublic - Flag to check if atlas is public.
 * @param {string} arg.label - The label of the task.
 * @param {string} arg.name - The name of the task.
 * @returns {number} Return the number of AI tasks with the same name.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const checkAtlasTaskName = ({ atlas, isPublic, label, name }) => {
  try {
    const atlasQuery = isPublic
      ? { publicId: atlasOps.getPublicId(atlas) }
      : { _id: atlasOps.getId(atlas) };
    const completeAtlas = Atlas.findOne(atlasQuery);
    const owner = atlasOps.getOwner(completeAtlas);
    const searchRegex = new RegExp(`(${name})(_)?([0-9]+)?`, 'i');
    const taskQuery = { name: searchRegex, label, owner };
    return Tasks.find(taskQuery).count();
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Gets the default FTP parameters for an AI task.
 *
 * @returns {string} Return the default FTP parameters for an AI task.
 */
export const getTaskFTPParameters = () => TASK_FTP_PARAMETERS;

export const runTask2 = async ({
  authenticate = true,
  debugIP,
  isTasker = true,
  itemForOwner = {},
  taskToRun
}) => {
  check(debugIP, String);
  try {
    if (authenticate) checkAuthentication();
    let userId = Meteor.userId();
    if (!isTasker) {
      let item = {};
      switch (itemForOwner.collectionName) {
        case 'atlas':
          item = Atlas.findOne(itemForOwner.id);
          break;
        case 'tasks':
          item = Tasks.findOne(itemForOwner.id);
          break;
        default:
          break;
      }
      userId = get(item, 'owner', userId);
    }
    const taskToSend = cloneDeep(taskToRun);
    const cleanedTask = await TaskSchemaOut.clean(taskToSend);

    const taskId = insert({ authenticate, taskObject: cleanedTask, userId });

    return taskId;
  } catch (exception) {
    handleMethodException(exception);
  }
};
